/**
 * Saved login credentials ("Remember me").
 *
 * Lets the login screen prefill on the next visit, across web and the Capacitor
 * (Android / iOS) apps. Uses @capacitor/preferences — native: app-sandboxed
 * storage; web: localStorage.
 *
 * Scope differs by platform, by design:
 *   • Web — store ONLY the username. The browser's own password manager handles
 *     the password (the form carries autocomplete=username/current-password), so
 *     we never write a password into XSS-readable localStorage.
 *   • Native (Android / iOS) — no browser password manager, and the store is
 *     app-private, so persist the password too for a true prefill / fast re-login.
 */
import { Preferences } from "@capacitor/preferences";
import { Capacitor } from "@capacitor/core";

const KEY = "icheck.savedCredentials";
const IS_NATIVE = Capacitor.isNativePlatform();

export interface SavedCredentials {
  loginId: string;
  /** Empty string on web — the browser password manager owns the password there. */
  password: string;
}

export async function loadSavedCredentials(): Promise<SavedCredentials | null> {
  try {
    const { value } = await Preferences.get({ key: KEY });
    if (!value) return null;
    const c = JSON.parse(value) as Partial<SavedCredentials>;
    if (c && typeof c.loginId === "string") {
      // Never surface a stored password on web (even one written by an older
      // build) — leave it to the browser password manager.
      return { loginId: c.loginId, password: IS_NATIVE && typeof c.password === "string" ? c.password : "" };
    }
  } catch {
    /* corrupt / unavailable storage — treat as none */
  }
  return null;
}

export async function saveCredentials(loginId: string, password: string): Promise<void> {
  try {
    // Web persists only the username; native persists both.
    const payload = IS_NATIVE ? { loginId, password } : { loginId, password: "" };
    await Preferences.set({ key: KEY, value: JSON.stringify(payload) });
  } catch {
    /* storage unavailable — silently skip */
  }
}

export async function clearSavedCredentials(): Promise<void> {
  try {
    await Preferences.remove({ key: KEY });
  } catch {
    /* ignore */
  }
}
